(ns nosco.ui.forms
  "Data-driven forms, used for now on the welcome page only.

  Given a form-description (form-desc) that can have containers
  and leaf elements, generate a long form. Future versions might
  do wizard-like pages but there was not time this time round.

  The form description isn't specced or validated in any way, it's
  a major downside.

  Major assumptions:

  * the title of the element is also the `name` in the html element. This is
  perfectly valid HTML and doesn't even need to be escaped anywhere.

  * all english

  * bootstrap styles

  * the `dest` key is either :meta, or a graphql-like nosco.user/foo key
    or empty, where :meta is implied. So far we can only collect givenName
    familyName and email as nosco.user keys.

  * No client-side validation for now -- we explicitly turn off the browser
    validation at the top-level form so we can show bootstrap style validation,
    that the server handles.

  * No need to render pre-filled forms so the components are uncontrolled.

  "
 (:require
  ["react" :as React]
  ["form-data-entries" :as formDataEntries]
  [clojure.edn :as edn]
  [clojure.string :as string]
  [hx.hooks :refer [useContext useState]]
  [hx.react :as hx :refer [defnc]]
  [nosco.polyfills]
  [nosco.ui.checkbox :as checkbox]
  [nosco.ui.inputs :as inputs]
  [nosco.ui.radio :as radio]
  [nosco.ui.select2 :as select2]
  [shadow.resource :as rc]))

(def countries (edn/read-string (rc/inline "countries.edn")))

(defn- merge-vec
  "Can be used with merge-with to ensure that the value is always a vector."
  [acc x]
  (if (vector? acc)
    (conj acc x)
    (vector acc x)))


(defn- form-data-entries [form]
  (if-not (undefined? (.-entries js/FormData.prototype))
    (.entries (js/FormData. form))
    (formDataEntries form)))

(defn form-data
  "Convert an HTML Form dom element into a CLJS map using FormData.entries.
  No conversion happens on the keys. Multiple elements with the same name will
  have their values be vectors, otherwise unique elements will be strings."
  [form]
  (->> (form-data-entries form)
       (seq)
       (map (fn [me]
              {(aget me 0)
               (aget me 1)}))
       (apply merge-with merge-vec)))


(defn map->urlencode
  "Convert a CLJS map to a url-encoded string"
  [m]
  (str
   (reduce (fn [^js url-search-params [k v]]
               (.append url-search-params (name k) v)
             url-search-params)
           (js/URLSearchParams.)
           m)))

(defn form-data-value-empty? [f k]
  (string/blank?
   (get f k)))

(def form-error-context (React/createContext {}))

(defn useFormError
  "Get the error text, if any, for the form element with this name."
  [name]
  (-> (useContext form-error-context)
      (get name)))

(defnc FormInputElement
  "A form input element"
  [{:keys [title name placeholder type dest pattern required help-text message on-change]}]
  (let [error (useFormError (or name title))]
    [inputs/FormField
     {:label title
      :error error
      :required? required
      :description help-text
      :help-text message}
     [inputs/TextInput
      {:type type
       :error error
       :required required
       :name (or name title)
       :pattern pattern
       :placeholder placeholder
       :autoComplete (case dest
                       :nosco.user/givenName "given-name"
                       :nosco.user/familyName "family-name"
                       :nosco.user/email "email"
                       nil)
       :on-change on-change}]]))

(defnc FormSingleSelect
  "Select a single value from a list. Implicitly adds an empty value to
   force user to make a choice."
  [{{:keys [title options help-text required]} :form-elem}]
  (let [error (useFormError title)]
    [inputs/FormField
     {:label title
      :error error
      :required? required
      :description help-text}
     [select2/Select {:error error
                      :name title}
      (for [opt options]
        [select2/Option {:label opt :value opt}])]]))

(defnc FormRadioSelect
  [{{:keys [title options help-text extra-textfield required]} :form-elem}]
  (let [error (useFormError title)
        textfield-cond (set (:when-on extra-textfield))
        [selected set-selected] (useState nil)
        on-change (fn [e]
                    (let [target (-> e (.-target))
                          value (.-value target)]
                      (set-selected value)))]
    [inputs/FormField
     {:label title
      :error error
      :required? required
      :description help-text}
     (for [opt options
           :let [checked (= opt selected)]]
       [:div
        [radio/Radio {:label opt
                      :checked checked
                      :name title
                      :wrap-label? true
                      :value opt
                      :on-change on-change
                      :id opt}]
        (when (and checked (textfield-cond selected))
          [inputs/TextInput
           {:placeholder opt
            :name title}])])]))

(defnc FormMultiSelect
  "A list of checkboxes where you can select more than one. Supports
   also an inline text input for cases like 'Other (please elaborate).
  These text inputs appear conditionally when their condition key is checked."
  [{{:keys [title options help-text extra-textfield required]} :form-elem}]
  (let [error (useFormError title)
        textfield-cond (set (:when-on extra-textfield))
        [visible-textfields set-visible-textfields!] (useState #{})
        on-change (fn [e]
                    (let [target (-> e (.-target))
                          id (.-id target)
                          value (.-checked target)]
                      (when (textfield-cond id)
                        (set-visible-textfields! (if value
                                                   conj disj) id))))]
    [inputs/FormField
     {:label title
      :error error
      :required? required
      :description help-text}
     (for [opt options]
       [:div
        [checkbox/CheckboxWithLabel
         {:label opt
          :name title
          :value opt
          :wrap-label? true
          :on-change on-change
          :id opt}]
        (when (visible-textfields opt)
          [inputs/TextInput
           {:placeholder opt
            :name title}])])]))

(defnc FormConsent
  "A list of checkboxes that are used to get user consent on various things.

   Can be required or optional. Required consent is governed by nosco.http.welcome/clean-field."
  [{{:keys [title name options help-text]} :form-elem}]
  (let [error (useFormError (or name title))]
    [inputs/FormField
     {:label title
      :error error
      :description help-text}
     (for [opt options
           :let [opt-title (:title opt)
                 opt-value (:value opt)
                 opt-id (:id opt)]]
       [checkbox/CheckboxWithLabel
        {:label opt-title
         :name (or name title)
         :wrap-label? true
         :value (or opt-value opt-title)
         :id (or opt-id opt-title)}])]))

(defnc FormElement [{:keys [form-elem class]}]
  (let [{:keys [title name type placeholder dest required help-text message on-change]
         :or {type :text}} form-elem]
    [:div {:class class}
     (case type
       :text [FormInputElement {:title title
                                :type "text"
                                :dest dest
                                :name name
                                :required required
                                :help-text help-text
                                :message message
                                :placeholder placeholder
                                :on-change on-change}]
       ;; TODO this doesn't validate anything so that pattern
       ;; is superfluous -- for now
       :year [FormInputElement {:title title
                                :type "text"
                                :name name
                                :dest dest
                                :required required
                                :help-text help-text
                                :message message
                                :pattern "(?:19|20)[0-9]{2}"
                                :placeholder placeholder
                                :on-change on-change}]
       ;; TODO could have an email pattern when we do client-side
       ;; validation
       :email [FormInputElement {:title title
                                 :type "email"
                                 :name name
                                 :required required
                                 :message message
                                 :help-text help-text
                                 :dest dest
                                 :placeholder placeholder
                                 :on-change on-change}]
       :password [FormInputElement {:title title
                                    :type "password"
                                    :name name
                                    :message message
                                    :required required
                                    :help-text help-text
                                    :dest dest
                                    :placeholder placeholder
                                    :on-change on-change}]
       :radio-select [FormRadioSelect {:form-elem form-elem}]
       :multi-select [FormMultiSelect {:form-elem form-elem}]
       :consent [FormConsent {:form-elem form-elem}]
       :select [FormSingleSelect {:form-elem form-elem}]
       ;; convenvience for country selector
       :country [FormSingleSelect {:form-elem (assoc form-elem
                                                     :options countries)}]
       [:div "Unknown element"])
     ]))

(declare FormBlock)

(defn form-block-or-elem [form-elem]
  (let [{:keys [form-block title]} form-elem]
    (cond
      form-block [FormBlock {:form-elem form-elem}]
      title [FormElement {:form-elem form-elem
                          :class "nosco-forms__row"}])))

(defnc FormBlock [{:keys [form-elem]}]
  (let [{:keys [form-block children]} form-elem]
    (case form-block
      ;; a page can contain rows
      :page
      [:div {:class "nosco-forms__page"}
       (for [x children]
         (form-block-or-elem x))]
      ;; a row can only contain leaf elements
      :row
      [:div {:class "nosco-forms__row"}
       (for [x children]
         [FormElement {:form-elem x}])])))


(defnc FormBody [{:keys [form-desc]}]
  [:<>
   (for [form-elem form-desc]
     (form-block-or-elem form-elem))])

(defnc Form [{:keys [action method on-submit form-desc form-errors form-id children]}]
  [:form {:action action
          :id form-id
          ;; turn off browser validation for patterns etc
          ;; so that we can control the styling and the messages
          :noValidate true
          :method method
          :on-submit on-submit}
   [:provider {:context form-error-context
               :value form-errors}
    [FormBody {:form-desc form-desc}]]
   children])
